const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Extract a human-readable error message from a FastAPI error response body.
// Handles string detail, validation-error arrays, nested objects, and fallbacks.
function extractDetail(body, fallback) {
  const d = body?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const msgs = d.map((e) => (typeof e === "string" ? e : e?.msg || JSON.stringify(e)));
    return msgs.join("; ") || fallback;
  }
  if (d && typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  if (typeof body?.message === "string") return body.message;
  return fallback;
}

// In-memory session token — fallback for browsers where httpOnly cookies
// are blocked by extensions, privacy settings, or proxy/CDN behaviour.
// Cleared on page unload; sessionStorage keeps it across SPA refreshes.
let _token = sessionStorage.getItem("lv_st") || null;

export function setSessionToken(t) {
  _token = t;
  if (t) sessionStorage.setItem("lv_st", t);
  else sessionStorage.removeItem("lv_st");
}

export function clearSessionToken() {
  _token = null;
  sessionStorage.removeItem("lv_st");
}

async function request(path, options = {}) {
  const url = `${API_BASE}/api${path}`;
  const headers = { "Content-Type": "application/json", ...options.headers };

  // Always attach Bearer token when available (redundant with cookie — belt & suspenders)
  if (_token) {
    headers["Authorization"] = `Bearer ${_token}`;
  }

  const res = await fetch(url, {
    ...options,
    credentials: "include",
    headers,
  });

  // 401 on any request → session gone → notify auth layer
  if (res.status === 401 && path !== "/auth/login") {
    clearSessionToken();
    window.dispatchEvent(new CustomEvent("lv-auth-expired"));
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(extractDetail(body, `Request failed (${res.status})`));
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: "POST", body: JSON.stringify(body) }),
  put: (path, body) => request(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: (path, body) => request(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: (path) => request(path, { method: "DELETE" }),
  upload: (path, formData) => {
    const url = `${API_BASE}/api${path}`;
    const headers = {};
    if (_token) headers["Authorization"] = `Bearer ${_token}`;
    return fetch(url, { method: "POST", credentials: "include", headers, body: formData })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const err = new Error(extractDetail(body, `Upload failed (${res.status})`));
          err.status = res.status;
          throw err;
        }
        return res.json();
      });
  },
};
